import React, { useState, useEffect, useCallback } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter, Routes, Route, useNavigate, useLocation } from 'react-router-dom';
import { HelmetProvider, Helmet } from 'react-helmet-async';
import { HomePage, SegmentPage, ExitPage, AboutPage, GuidesPage, CaseStudyCaprock, Footer, segmentData } from './sections.jsx';
import { SITE_URL, DEFAULT_OG_IMAGE, META_BY_ID } from './seo-meta.js';

// --- Per-route SEO: title, description, canonical, Open Graph + Twitter cards ---
const Seo = ({ id }) => {
  const meta = META_BY_ID[id] || META_BY_ID.home;
  const url = SITE_URL + (meta.path === '/' ? '' : meta.path);
  const image = SITE_URL + (meta.image || DEFAULT_OG_IMAGE);
  return (
    <Helmet prioritizeSeoTags>
      <title>{meta.title}</title>
      <meta name="description" content={meta.description} />
      <link rel="canonical" href={url} />
      <meta name="robots" content={meta.noindex ? "noindex, nofollow" : "index, follow"} />
      {/* Open Graph */}
      <meta property="og:type" content="website" />
      <meta property="og:site_name" content="Monitored Growth" />
      <meta property="og:title" content={meta.title} />
      <meta property="og:description" content={meta.description} />
      <meta property="og:url" content={url} />
      <meta property="og:image" content={image} />
      {/* Twitter card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={meta.title} />
      <meta name="twitter:description" content={meta.description} />
      <meta name="twitter:image" content={image} />
    </Helmet>
  );
};

// --- Nav ---
const Nav = ({ onCta, current, goTo, mobileOpen, setMobileOpen }) => {
  const links = [
    { id: "residential", l: "Residential" },
    { id: "mixed", l: "Mixed" },
    { id: "commercial", l: "Commercial" },
    { id: "exit", l: "Exit Accelerator" },
    { id: "guides", l: "Guides" },
    { id: "about", l: "About" }
  ];

  return (
    <>
      <nav className="nav">
        <div className="nav-inner">
          <div className="nav-logo">
            <button onClick={()=>goTo("home")} style={{background:"transparent",border:0,cursor:"pointer",padding:0,display:"flex",alignItems:"center"}} aria-label="Monitored Growth home">
              <img src="/assets/logo.png" alt=""/>
            </button>
            <span className="nav-mark">
              <button onClick={()=>goTo("home")} style={{display:"block",lineHeight:1,background:"transparent",border:0,padding:0,cursor:"pointer",font:"inherit",color:"inherit",textAlign:"left"}}>Monitored Growth</button>
              <a className="dr-link" href="https://www.digitalrodeo.agency/" target="_blank" rel="noopener noreferrer" style={{display:"block",fontFamily:"var(--f-mono)",fontSize:"9.5px",letterSpacing:".18em",color:"var(--mute)",textTransform:"uppercase",marginTop:"3px",fontWeight:400}}>A Digital Rodeo brand</a>
            </span>
          </div>
          <div className="nav-links">
            {links.map((x) => (
              <button key={x.id} className={current===x.id?"active":""} onClick={()=>goTo(x.id)}>{x.l}</button>
            ))}
          </div>
          <div className="nav-right">
            <button className="btn btn-primary btn-sm" onClick={onCta}>Book a call →</button>
            <button className="nav-toggle" onClick={()=>setMobileOpen(v=>!v)} aria-label="Menu">
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
                {mobileOpen ? <><path d="M6 6l12 12"/><path d="M18 6 6 18"/></> : <><path d="M4 7h16"/><path d="M4 12h16"/><path d="M4 17h16"/></>}
              </svg>
            </button>
          </div>
        </div>
      </nav>
      {mobileOpen && (
        <div className="mobile-menu" onClick={()=>setMobileOpen(false)}>
          <div className="container">
            {links.map(x => (
              <button key={x.id} onClick={()=>{goTo(x.id);setMobileOpen(false);}}>{x.l}</button>
            ))}
            <button className="btn btn-primary" onClick={onCta}>Book a 30-minute call</button>
          </div>
        </div>
      )}
    </>
  );
};

// --- Modal (book a call) ---
const FitCallModal = ({ open, onClose }) => {
  const [form, setForm] = useState({ name:"", email:"", company:"", revenue:"", timeline:"", message:"" });
  const [errs, setErrs] = useState({});
  const [sent, setSent] = useState(false);

  useEffect(() => {
    if (open) { setSent(false); setErrs({}); document.body.style.overflow = "hidden"; }
    else { document.body.style.overflow = ""; }
    const esc = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", esc);
    return () => window.removeEventListener("keydown", esc);
  }, [open, onClose]);

  if (!open) return null;

  const submit = (e) => {
    e.preventDefault();
    const v = {};
    if (!form.name.trim()) v.name = "Required";
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.email)) v.email = "Valid email required";
    if (!form.company.trim()) v.company = "Required";
    if (!form.revenue) v.revenue = "Pick a range";
    setErrs(v);
    if (Object.keys(v).length !== 0) return;

    fetch("/api/lead", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ ...form, formType: "book-call" }),
      keepalive: true,
    }).catch(err => console.error("book-call lead post failed", err));

    setSent(true);
  };

  return (
    <div className="modal-bg" onClick={onClose}>
      <div className="modal" onClick={e=>e.stopPropagation()}>
        <button className="modal-close" onClick={onClose} aria-label="Close">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"><path d="M6 6l12 12"/><path d="M18 6 6 18"/></svg>
        </button>
        {!sent ? (
          <>
            <div className="eyebrow accent">Book a call</div>
            <h3 style={{marginTop:14}}>30 minutes. We'll review your numbers and tell you which tier fits.</h3>
            <p className="sub" style={{marginTop:12}}>Including whether you should be talking to us at all.</p>
            <form onSubmit={submit} noValidate>
              <div className={`field ${errs.name?"err":""}`} style={{marginBottom:14}}>
                <label>Name</label>
                <input value={form.name} onChange={e=>setForm({...form,name:e.target.value})} placeholder="Your name"/>
                {errs.name && <div className="field-err">{errs.name}</div>}
              </div>
              <div className={`field ${errs.email?"err":""}`} style={{marginBottom:14}}>
                <label>Work email</label>
                <input type="email" value={form.email} onChange={e=>setForm({...form,email:e.target.value})}/>
                {errs.email && <div className="field-err">{errs.email}</div>}
              </div>
              <div className={`field ${errs.company?"err":""}`} style={{marginBottom:14}}>
                <label>Company</label>
                <input value={form.company} onChange={e=>setForm({...form,company:e.target.value})}/>
                {errs.company && <div className="field-err">{errs.company}</div>}
              </div>
              <div className={`field ${errs.revenue?"err":""}`} style={{marginBottom:14}}>
                <label>Annual revenue</label>
                <select value={form.revenue} onChange={e=>setForm({...form,revenue:e.target.value})}>
                  <option value="">Select…</option>
                  <option>Under $1M</option><option>$1M – $3M</option><option>$3M – $5M</option><option>$5M – $10M</option><option>Over $10M</option>
                </select>
                {errs.revenue && <div className="field-err">{errs.revenue}</div>}
              </div>
              <div className="field" style={{marginBottom:18}}>
                <label>What's prompting the call? (optional)</label>
                <textarea rows="3" value={form.message} onChange={e=>setForm({...form,message:e.target.value})}/>
              </div>
              <button className="btn btn-primary" type="submit" style={{width:"100%",justifyContent:"center"}}>Request my call →</button>
            </form>
          </>
        ) : (
          <div>
            <div className="eyebrow accent">Received</div>
            <h3 style={{marginTop:14}}>Thank you, {form.name.split(" ")[0]}.</h3>
            <p className="sub" style={{marginTop:10}}>We'll reply within one business day with two time slots. Look for an email from <strong style={{color:"var(--cream)"}}>hello@monitoredgrowth.com</strong>.</p>
            <button className="btn btn-primary" onClick={onClose} style={{width:"100%",justifyContent:"center",marginTop:18}}>Close</button>
          </div>
        )}
      </div>
    </div>
  );
};

// --- Modal (guide lead capture) ---
const GuideLeadModal = ({ open, guide, onClose }) => {
  const [form, setForm] = useState({ name:"", email:"", company:"", role:"", revenue:"", segment:"" });
  const [errs, setErrs] = useState({});

  useEffect(() => {
    if (open) { setErrs({}); setForm({ name:"", email:"", company:"", role:"", revenue:"", segment:"" }); document.body.style.overflow = "hidden"; }
    else { document.body.style.overflow = ""; }
    const esc = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", esc);
    return () => window.removeEventListener("keydown", esc);
  }, [open, onClose]);

  if (!open || !guide) return null;

  const submit = (e) => {
    e.preventDefault();
    const v = {};
    if (!form.name.trim()) v.name = "Required";
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.email)) v.email = "Valid email required";
    if (!form.company.trim()) v.company = "Required";
    if (!form.role) v.role = "Pick one";
    if (!form.revenue) v.revenue = "Pick a range";
    if (!form.segment) v.segment = "Pick one";
    setErrs(v);
    if (Object.keys(v).length !== 0) return;

    // Fire-and-forget to CRM. Never block the value delivery on a CRM hiccup.
    fetch("/api/lead", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        ...form,
        guideNo: guide.no,
        guideTitle: guide.title,
      }),
      keepalive: true,
    }).catch(err => console.error("lead post failed", err));

    window.open(guide.url, "_blank", "noopener");
    onClose();
  };

  return (
    <div className="modal-bg" onClick={onClose}>
      <div className="modal" onClick={e=>e.stopPropagation()}>
        <button className="modal-close" onClick={onClose} aria-label="Close">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"><path d="M6 6l12 12"/><path d="M18 6 6 18"/></svg>
        </button>
        <div className="eyebrow accent">Field Guide № {guide.no}</div>
        <h3 style={{marginTop:14}}>{guide.title}</h3>
        <p className="sub" style={{marginTop:12}}>Tell us who you are and we'll open your guide as a print-ready PDF you can save or share.</p>
        <form onSubmit={submit} noValidate style={{marginTop:18}}>
          <div className={`field ${errs.name?"err":""}`} style={{marginBottom:14}}>
            <label>Name</label>
            <input value={form.name} onChange={e=>setForm({...form,name:e.target.value})} placeholder="Your name"/>
            {errs.name && <div className="field-err">{errs.name}</div>}
          </div>
          <div className={`field ${errs.email?"err":""}`} style={{marginBottom:14}}>
            <label>Work email</label>
            <input type="email" value={form.email} onChange={e=>setForm({...form,email:e.target.value})}/>
            {errs.email && <div className="field-err">{errs.email}</div>}
          </div>
          <div className={`field ${errs.company?"err":""}`} style={{marginBottom:14}}>
            <label>Company</label>
            <input value={form.company} onChange={e=>setForm({...form,company:e.target.value})}/>
            {errs.company && <div className="field-err">{errs.company}</div>}
          </div>
          <div className={`field ${errs.role?"err":""}`} style={{marginBottom:14}}>
            <label>Your role</label>
            <select value={form.role} onChange={e=>setForm({...form,role:e.target.value})}>
              <option value="">Select…</option>
              <option>Owner</option>
              <option>Partner</option>
              <option>Marketing lead</option>
              <option>Other</option>
            </select>
            {errs.role && <div className="field-err">{errs.role}</div>}
          </div>
          <div className={`field ${errs.revenue?"err":""}`} style={{marginBottom:14}}>
            <label>Annual revenue</label>
            <select value={form.revenue} onChange={e=>setForm({...form,revenue:e.target.value})}>
              <option value="">Select…</option>
              <option>Under $1M</option><option>$1M – $3M</option><option>$3M – $5M</option><option>$5M – $10M</option><option>Over $10M</option>
            </select>
            {errs.revenue && <div className="field-err">{errs.revenue}</div>}
          </div>
          <div className={`field ${errs.segment?"err":""}`} style={{marginBottom:18}}>
            <label>Segment</label>
            <select value={form.segment} onChange={e=>setForm({...form,segment:e.target.value})}>
              <option value="">Select…</option>
              <option>Residential</option>
              <option>Mixed (res + commercial)</option>
              <option>Commercial</option>
              <option>Preparing to sell (24–48 months)</option>
            </select>
            {errs.segment && <div className="field-err">{errs.segment}</div>}
          </div>
          <button className="btn btn-primary" type="submit" style={{width:"100%",justifyContent:"center"}}>Open the guide →</button>
        </form>
      </div>
    </div>
  );
};

// --- AI Visibility Report (lead magnet): shared form pieces ---
const AUDIT_INIT = { name:"", email:"", company:"", city:"", website:"", segment:"" };

const validateAudit = (form) => {
  const v = {};
  if (!form.name.trim()) v.name = "Required";
  if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.email)) v.email = "Valid email required";
  if (!form.company.trim()) v.company = "Required";
  if (!form.city.trim()) v.city = "Required";
  return v;
};

const postAudit = (form) =>
  fetch("/api/lead", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ ...form, formType: "ai-visibility-audit" }),
    keepalive: true,
  }).catch(err => console.error("audit lead post failed", err));

const AuditFields = ({ form, setForm, errs }) => (
  <>
    <div className={`field ${errs.name?"err":""}`} style={{marginBottom:14}}>
      <label>Name</label>
      <input value={form.name} onChange={e=>setForm({...form,name:e.target.value})} placeholder="Your name"/>
      {errs.name && <div className="field-err">{errs.name}</div>}
    </div>
    <div className={`field ${errs.email?"err":""}`} style={{marginBottom:14}}>
      <label>Work email</label>
      <input type="email" value={form.email} onChange={e=>setForm({...form,email:e.target.value})}/>
      {errs.email && <div className="field-err">{errs.email}</div>}
    </div>
    <div className={`field ${errs.company?"err":""}`} style={{marginBottom:14}}>
      <label>Company</label>
      <input value={form.company} onChange={e=>setForm({...form,company:e.target.value})}/>
      {errs.company && <div className="field-err">{errs.company}</div>}
    </div>
    <div className={`field ${errs.city?"err":""}`} style={{marginBottom:14}}>
      <label>City / market you serve</label>
      <input value={form.city} onChange={e=>setForm({...form,city:e.target.value})} placeholder="e.g. Tulsa, OK"/>
      {errs.city && <div className="field-err">{errs.city}</div>}
    </div>
    <div className="field" style={{marginBottom:14}}>
      <label>Website (optional)</label>
      <input value={form.website} onChange={e=>setForm({...form,website:e.target.value})} placeholder="yourcompany.com"/>
    </div>
    <div className="field" style={{marginBottom:18}}>
      <label>Segment (optional)</label>
      <select value={form.segment} onChange={e=>setForm({...form,segment:e.target.value})}>
        <option value="">Select…</option>
        <option>Residential</option>
        <option>Mixed (res + commercial)</option>
        <option>Commercial</option>
        <option>Preparing to sell (24–48 months)</option>
      </select>
    </div>
  </>
);

// --- Modal (AI Visibility Report lead magnet) ---
const AuditModal = ({ open, onClose }) => {
  const [form, setForm] = useState(AUDIT_INIT);
  const [errs, setErrs] = useState({});
  const [sent, setSent] = useState(false);

  useEffect(() => {
    if (open) { setSent(false); setErrs({}); document.body.style.overflow = "hidden"; }
    else { document.body.style.overflow = ""; }
    const esc = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", esc);
    return () => window.removeEventListener("keydown", esc);
  }, [open, onClose]);

  if (!open) return null;

  const submit = (e) => {
    e.preventDefault();
    const v = validateAudit(form);
    setErrs(v);
    if (Object.keys(v).length !== 0) return;
    postAudit(form);
    setSent(true);
  };

  return (
    <div className="modal-bg" onClick={onClose}>
      <div className="modal" onClick={e=>e.stopPropagation()}>
        <button className="modal-close" onClick={onClose} aria-label="Close">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"><path d="M6 6l12 12"/><path d="M18 6 6 18"/></svg>
        </button>
        {!sent ? (
          <>
            <div className="eyebrow accent">Free AI Visibility Report</div>
            <h3 style={{marginTop:14}}>See what ChatGPT, Gemini, and Google's AI answers say about your company today.</h3>
            <p className="sub" style={{marginTop:12}}>We run the questions your buyers ask AI, in your market, and send you a one-page report: where you show up, where you don't, and who the AI recommends instead.</p>
            <form onSubmit={submit} noValidate style={{marginTop:18}}>
              <AuditFields form={form} setForm={setForm} errs={errs}/>
              <button className="btn btn-primary" type="submit" style={{width:"100%",justifyContent:"center"}}>Send my AI Visibility Report →</button>
            </form>
          </>
        ) : (
          <div>
            <div className="eyebrow accent">Received</div>
            <h3 style={{marginTop:14}}>Thank you, {form.name.split(" ")[0]}.</h3>
            <p className="sub" style={{marginTop:10}}>We'll run your AI Visibility Report and email it within two business days. Look for an email from <strong style={{color:"var(--cream)"}}>hello@monitoredgrowth.com</strong>.</p>
            <button className="btn btn-primary" onClick={onClose} style={{width:"100%",justifyContent:"center",marginTop:18}}>Close</button>
          </div>
        )}
      </div>
    </div>
  );
};

// --- Standalone /audit landing page (Focus 30 outreach) ---
const AuditPage = () => {
  const [form, setForm] = useState(AUDIT_INIT);
  const [errs, setErrs] = useState({});
  const [sent, setSent] = useState(false);

  const submit = (e) => {
    e.preventDefault();
    const v = validateAudit(form);
    setErrs(v);
    if (Object.keys(v).length !== 0) return;
    postAudit(form);
    setSent(true);
  };

  const goToMain = (id) => { window.location.href = id === "home" ? "/" : "/" + id; };

  return (
    <>
      <header style={{borderBottom:"1px solid var(--line)",padding:"18px 0"}}>
        <div className="container" style={{display:"flex",alignItems:"center",justifyContent:"space-between"}}>
          <a href="/" style={{display:"flex",alignItems:"center",gap:10,textDecoration:"none"}} aria-label="Monitored Growth home">
            <img src="/assets/logo.png" alt="" style={{height:28}}/>
            <span style={{fontFamily:"var(--f-mono)",fontSize:13,letterSpacing:".04em",color:"var(--cream)"}}>Monitored Growth</span>
          </a>
          <a href="/" style={{fontFamily:"var(--f-mono)",fontSize:12,letterSpacing:".04em",color:"var(--mute)",textDecoration:"none"}}>monitoredgrowth.com →</a>
        </div>
      </header>

      <section className="hero" style={{paddingTop:80,paddingBottom:36}}>
        <div className="container">
          <div className="eyebrow accent">Free AI Visibility Report</div>
          <h1 style={{marginTop:18,fontSize:"clamp(36px,4.6vw,64px)",maxWidth:"22ch"}}>
            When a homeowner asks AI for a security company, does your name come up?
          </h1>
          <p className="lede" style={{marginTop:24,maxWidth:"62ch"}}>
            Your buyers are asking ChatGPT, Gemini, and Google's AI answers who to trust in your market. We run those exact questions and send you a one-page report: where you show up, where you don't, and who the AI recommends instead.
          </p>
        </div>
      </section>

      <section className="section no-top">
        <div className="container">
          <div className="split-2">
            <div>
              <div className="eyebrow">What's in the report</div>
              <ul style={{marginTop:20,paddingLeft:20,display:"grid",gap:12,color:"var(--cream-2)",lineHeight:1.55}}>
                <li>The questions we ask in your city: "best alarm company in [city]," "most trusted," "[you] vs the national brand."</li>
                <li>Whether each engine names you, mentions you, or skips you.</li>
                <li>Who gets recommended instead, and why the AI trusts them.</li>
                <li>Whether your hours, services, and phone are accurate where the AI reads them.</li>
                <li>The three moves that close the gap fastest.</li>
              </ul>
              <p className="body-prose" style={{marginTop:28}}>
                I ran marketing inside a national security brand for years. I know how they win the search results, and how a dealer doing $1M to $10M takes that ground back in their own market without a national budget.
              </p>
            </div>

            <div>
              {sent ? (
                <div className="form-wrap form-success">
                  <div className="eyebrow accent">Received</div>
                  <h3 style={{marginTop:14,marginBottom:8}}>Thank you, {form.name.split(" ")[0] || "there"}.</h3>
                  <p className="body-prose">We'll run your AI Visibility Report and email it within two business days. Look for a note from <strong style={{color:"var(--cream)"}}>hello@monitoredgrowth.com</strong>.</p>
                </div>
              ) : (
                <form className="form-wrap" onSubmit={submit} noValidate>
                  <div className="eyebrow accent">Get your report</div>
                  <h3 style={{marginTop:12,marginBottom:18}}>Free, and yours in two business days.</h3>
                  <AuditFields form={form} setForm={setForm} errs={errs}/>
                  <button className="btn btn-primary" type="submit" style={{width:"100%",justifyContent:"center"}}>Send my AI Visibility Report →</button>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>

      <Footer goTo={goToMain}/>
    </>
  );
};

// --- Tweaks panel ---
const Tweaks = ({ tweaks, setTweak, visible }) => {
  if (!visible) return null;
  const accents = ["#F26B1F","#E04E1A","#D9A441","#7A7A82","#F5F2ED"];
  return (
    <div className="tweaks">
      <h4>Tweaks</h4>
      <div className="row">
        <label>Accent color</label>
        <div className="swatches">
          {accents.map(c => (
            <button key={c} aria-label={c} className={`sw ${tweaks.accent===c?"on":""}`} style={{background:c}} onClick={()=>setTweak("accent",c)}/>
          ))}
        </div>
      </div>
      <div className="row">
        <label>Density</label>
        <div className={`toggle ${tweaks.dense?"on":""}`} onClick={()=>setTweak("dense",!tweaks.dense)}/>
      </div>
    </div>
  );
};

// --- 404 ---
const NotFound = ({ goTo }) => (
  <section className="hero" style={{paddingTop:140,paddingBottom:100}}>
    <div className="container-narrow" style={{textAlign:"center"}}>
      <div className="eyebrow accent" style={{justifyContent:"center"}}>404</div>
      <h1 style={{marginTop:24,fontSize:"clamp(36px,4.6vw,64px)"}}>This page wandered off.</h1>
      <p className="lede" style={{marginTop:24,marginLeft:"auto",marginRight:"auto"}}>
        The page you're looking for doesn't exist. Head back to the start.
      </p>
      <button className="btn btn-primary" style={{marginTop:28}} onClick={()=>goTo("home")}>Back to home →</button>
    </div>
  </section>
);

// --- Path <-> route-id helpers ---
const idToPath = (id) => (id === "home" ? "/" : "/" + id);
const KNOWN_HASH_ROUTES = new Set(["residential","mixed","commercial","exit","about","guides"]);

// --- Main site shell: nav, footer, modals, and the routed pages ---
const Site = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const [tweaks, setTweaksState] = useState(window.__TWEAK_DEFAULTS);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [auditOpen, setAuditOpen] = useState(false);
  const [pendingGuide, setPendingGuide] = useState(null);
  const [tweaksVisible, setTweaksVisible] = useState(false);

  // Active route id for nav highlighting, derived from the URL.
  const current = location.pathname === "/" ? "home" : location.pathname.replace(/^\/+|\/+$/g, "");

  const goTo = useCallback((id) => {
    navigate(idToPath(id));
  }, [navigate]);

  // Reset scroll on every route change (covers nav clicks, back/forward, deep links).
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
  }, [location.pathname]);

  // Legacy hash URLs (e.g. /#mixed) linked from elsewhere → redirect to the real path.
  // Hash fragments never reach the server, so this has to happen client-side.
  useEffect(() => {
    const h = window.location.hash.replace("#", "");
    if (KNOWN_HASH_ROUTES.has(h)) {
      navigate(idToPath(h), { replace: true });
      window.history.replaceState(null, "", window.location.pathname);
    }
  }, [navigate]);

  const setTweak = useCallback((k,v) => {
    setTweaksState(prev => {
      const next = { ...prev, [k]:v };
      window.parent.postMessage({type:"__edit_mode_set_keys", edits:{[k]:v}}, "*");
      return next;
    });
  }, []);

  useEffect(() => {
    const handler = (e) => {
      if (!e.data) return;
      if (e.data.type === "__activate_edit_mode") setTweaksVisible(true);
      if (e.data.type === "__deactivate_edit_mode") setTweaksVisible(false);
    };
    window.addEventListener("message", handler);
    window.parent.postMessage({type:"__edit_mode_available"}, "*");
    return () => window.removeEventListener("message", handler);
  }, []);

  useEffect(() => {
    document.documentElement.style.setProperty("--orange", tweaks.accent);
  }, [tweaks.accent]);

  const openCta = () => setModalOpen(true);
  const openAudit = () => setAuditOpen(true);

  return (
    <>
      <Nav onCta={openCta} current={current} goTo={goTo} mobileOpen={mobileOpen} setMobileOpen={setMobileOpen}/>
      <main key={location.pathname}>
        <Routes>
          <Route path="/" element={<><Seo id="home"/><HomePage onCta={openCta} onAudit={openAudit} goTo={goTo} tweaks={tweaks}/></>}/>
          <Route path="/residential" element={<><Seo id="residential"/><SegmentPage data={segmentData.residential} onCta={openCta} goTo={goTo}/></>}/>
          <Route path="/mixed" element={<><Seo id="mixed"/><SegmentPage data={segmentData.mixed} onCta={openCta} goTo={goTo}/></>}/>
          <Route path="/commercial" element={<><Seo id="commercial"/><SegmentPage data={segmentData.commercial} onCta={openCta} goTo={goTo}/></>}/>
          <Route path="/exit" element={<><Seo id="exit"/><ExitPage onCta={openCta}/></>}/>
          <Route path="/about" element={<><Seo id="about"/><AboutPage onCta={openCta}/></>}/>
          <Route path="/guides" element={<><Seo id="guides"/><GuidesPage onGuide={(g)=>setPendingGuide(g)}/></>}/>
          <Route path="/case-studies/caprock-rentals" element={<><Seo id="caprock-rentals"/><CaseStudyCaprock onCta={openCta} goTo={goTo}/></>}/>
          <Route path="*" element={<><Seo id="home"/><NotFound goTo={goTo}/></>}/>
        </Routes>
      </main>
      <Footer goTo={goTo}/>
      <FitCallModal open={modalOpen} onClose={()=>setModalOpen(false)}/>
      <AuditModal open={auditOpen} onClose={()=>setAuditOpen(false)}/>
      <GuideLeadModal open={!!pendingGuide} guide={pendingGuide} onClose={()=>setPendingGuide(null)}/>
      <Tweaks tweaks={tweaks} setTweak={setTweak} visible={tweaksVisible}/>
    </>
  );
};

// --- App: top-level routing. /audit is a standalone landing page (own minimal chrome). ---
// (React Router v6 matches trailing slashes leniently, so "/audit" also serves "/audit/".)
const App = () => (
  <Routes>
    <Route path="/audit" element={<main key="audit"><Seo id="audit"/><AuditPage/></main>}/>
    <Route path="*" element={<Site/>}/>
  </Routes>
);

createRoot(document.getElementById("root")).render(
  <HelmetProvider>
    <BrowserRouter>
      <App/>
    </BrowserRouter>
  </HelmetProvider>
);
